import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  CockpitSettings,
  DebtInstrument,
  OwnerDistribution,
  OwnerDrawKind,
} from '../types/finances';
import { DEFAULT_COCKPIT_SETTINGS } from '../types/finances';

const SETTINGS_DOC = 'cockpitSettings';

function tsToDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate();
  }
  return new Date();
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asOwnerPositionIds(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

export function parseCockpitSettings(data: Record<string, unknown> | undefined): CockpitSettings {
  if (!data) return { ...DEFAULT_COCKPIT_SETTINGS };
  return {
    minimumCash: Math.max(0, asNumber(data.minimumCash, 0)),
    compensationNotes: typeof data.compensationNotes === 'string' ? data.compensationNotes : '',
    ownerPositionIds: asOwnerPositionIds(data.ownerPositionIds),
    updatedAt: data.updatedAt ? tsToDate(data.updatedAt) : null,
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : undefined,
  };
}

export function subscribeCockpitSettings(
  onNext: (settings: CockpitSettings) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'financeSettings', SETTINGS_DOC),
    (snap) => onNext(parseCockpitSettings(snap.data() as Record<string, unknown> | undefined)),
    (err) => onError?.(err),
  );
}

export async function saveCockpitSettings(
  settings: CockpitSettings,
  updatedBy: string,
): Promise<void> {
  await setDoc(doc(db, 'financeSettings', SETTINGS_DOC), {
    minimumCash: settings.minimumCash,
    compensationNotes: settings.compensationNotes,
    ownerPositionIds: settings.ownerPositionIds ?? null,
    updatedAt: serverTimestamp(),
    updatedBy,
  }, { merge: true });
}

function parseDrawKind(value: unknown): OwnerDrawKind {
  if (value === 'tax_cover' || value === 'profit' || value === 'other') return value;
  return 'other';
}

function mapDraw(id: string, data: Record<string, unknown>): OwnerDistribution {
  return {
    id,
    date: tsToDate(data.date),
    amount: asNumber(data.amount, 0),
    kind: parseDrawKind(data.kind),
    note: typeof data.note === 'string' ? data.note : '',
    createdAt: tsToDate(data.createdAt),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
  };
}

export function subscribeOwnerDistributions(
  onNext: (draws: OwnerDistribution[]) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    collection(db, 'ownerDistributions'),
    (snap) => {
      const rows = snap.docs.map((d) => mapDraw(d.id, d.data() as Record<string, unknown>));
      rows.sort((a, b) => b.date.getTime() - a.date.getTime());
      onNext(rows);
    },
    (err) => onError?.(err),
  );
}

export async function addOwnerDistribution(
  draw: Omit<OwnerDistribution, 'id' | 'createdAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, 'ownerDistributions'), {
    date: draw.date,
    amount: draw.amount,
    kind: draw.kind,
    note: draw.note,
    createdAt: serverTimestamp(),
    createdBy: draw.createdBy,
  });
  return ref.id;
}

export async function deleteOwnerDistribution(id: string): Promise<void> {
  await deleteDoc(doc(db, 'ownerDistributions', id));
}

function mapDebt(id: string, data: Record<string, unknown>): DebtInstrument {
  return {
    id,
    name: typeof data.name === 'string' ? data.name : '',
    lender: typeof data.lender === 'string' ? data.lender : '',
    principalRemaining: asNumber(data.principalRemaining, 0),
    annualRatePct: asNumber(data.annualRatePct, 0),
    paymentAmount: asNumber(data.paymentAmount, 0),
    paymentDay: Math.min(28, Math.max(1, asNumber(data.paymentDay, 1))),
    remainingPayments: data.remainingPayments == null ? null : asNumber(data.remainingPayments, 0),
    balloonAmount: asNumber(data.balloonAmount, 0),
    balloonDate: data.balloonDate ? tsToDate(data.balloonDate) : null,
    notes: typeof data.notes === 'string' ? data.notes : '',
    isActive: asBool(data.isActive, true),
    alreadyInBurn: asBool(data.alreadyInBurn, true),
    createdAt: tsToDate(data.createdAt),
    updatedAt: tsToDate(data.updatedAt),
  };
}

export function subscribeDebtInstruments(
  onNext: (rows: DebtInstrument[]) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    collection(db, 'debtInstruments'),
    (snap) => {
      const rows = snap.docs.map((d) => mapDebt(d.id, d.data() as Record<string, unknown>));
      rows.sort((a, b) => (a.name || a.lender).localeCompare(b.name || b.lender));
      onNext(rows);
    },
    (err) => onError?.(err),
  );
}

export async function addDebtInstrument(
  row: Omit<DebtInstrument, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const ref = await addDoc(collection(db, 'debtInstruments'), {
    ...row,
    balloonDate: row.balloonDate ?? null,
    remainingPayments: row.remainingPayments ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateDebtInstrument(
  id: string,
  patch: Partial<Omit<DebtInstrument, 'id' | 'createdAt'>>,
): Promise<void> {
  await updateDoc(doc(db, 'debtInstruments', id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteDebtInstrument(id: string): Promise<void> {
  await deleteDoc(doc(db, 'debtInstruments', id));
}
